import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CategoryIcon from 'components/CategoryIcon';
import { categories as DEFAULT_CATS } from 'utils/categories';
import { getMacroName, getMicroName } from 'utils/getCategoryData';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

// -------- helpers --------
const subsOf = (cat) =>
  cat?.micro || cat?.microCategories || cat?.children || [];

const normalizeToSelections = (value) => {
  if (!value) return { macros: [], micros: [] };

  if (Array.isArray(value)) {
    const macros = [];
    const micros = [];
    value.forEach((g) => {
      if (!g) return;
      const macro = g.macroCategory ?? g.macroCategoryId;
      const micro = g.microCategory ?? g.microCategoryIds;
      if (typeof macro === 'number') {
        macros.push(macro);
        if (Array.isArray(micro)) micros.push(...micro);
      }
    });
    return { macros: [...new Set(macros)], micros: [...new Set(micros)] };
  }

  if (typeof value === 'object') {
    return {
      macros: Array.isArray(value.macros) ? [...new Set(value.macros)] : [],
      micros: Array.isArray(value.micros) ? [...new Set(value.micros)] : [],
    };
  }
  return { macros: [], micros: [] };
};
// -------------------------

const CategorySelectorGrid = ({
  noTitle = false,

  // optional override of catalog
  categories,

  // controlled API (interactive mode)
  selectedMacros,
  setSelectedMacros,
  selectedMicros,
  setSelectedMicros,

  // optional controlled scope for micros
  showAllMicros,
  setShowAllMicros,
  // uncontrolled: start with every subcategory on screen
  initialShowAllMicros = false,

  // prefill (either {macros,micros} or categoriesAllowed[])
  initialCategories,

  // display/behavior flags
  restrictToSelected = false, // show only what's in initialCategories
  readOnly = false, // disables toggles + hides toolbar
  showMicros = true, // hide micros section entirely

  // NEW: hide micro section title
  noMicroTitle = false,
  white = false,
  // NEW: size control ('xs' | 'sm' | 'md' | 'lg')
  size = 'md',

  // extras for append-only behavior
  allowMacroSelect = true,
  allowMicroSelect = true,
  lockedMacros = [],
  lockedMicros = [],
}) => {
  const { t } = useTranslation();
  const CATS = categories ?? DEFAULT_CATS;

  // In restrict or readOnly we render strictly from initialCategories
  const restrict = restrictToSelected || readOnly;

  // controlled vs internal state
  const controlledMacros =
    selectedMacros !== undefined && typeof setSelectedMacros === 'function';
  const controlledMicros =
    selectedMicros !== undefined && typeof setSelectedMicros === 'function';

  const [macrosState, setMacrosState] = useState([]);
  const [microsState, setMicrosState] = useState([]);

  const macros = controlledMacros ? selectedMacros : macrosState;
  const setMacros = controlledMacros ? setSelectedMacros : setMacrosState;

  const micros = controlledMicros ? selectedMicros : microsState;
  const setMicros = controlledMicros ? setSelectedMicros : setMicrosState;

  const normInit = useMemo(
    () => normalizeToSelections(initialCategories),
    [initialCategories]
  );

  // seed internal state from initialCategories in interactive mode
  useEffect(() => {
    if (restrict) return;
    if (!controlledMacros) setMacros(normInit.macros || []);
    if (!controlledMicros) setMicros(normInit.micros || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    restrict,
    normInit.macros,
    normInit.micros,
    controlledMacros,
    controlledMicros,
  ]);

  // Which subcategories are on screen (only meaningful interactively):
  //   'selected'  those of the chosen categories (the default)
  //   'all'       every subcategory ("Mostrar todas")
  //   'none'      none at all ("Ocultar todas")
  // "Ocultar todas" used to fall back to 'selected', which left the chosen
  // categories' subcategories showing — it looked like it hid only some.
  // The controlled API is a boolean and keeps its old all/selected toggle.
  const [internalView, setInternalView] = useState(
    initialShowAllMicros ? 'all' : 'selected'
  );
  const ctrlShowAll =
    typeof showAllMicros === 'boolean' &&
    typeof setShowAllMicros === 'function';
  const microView = ctrlShowAll
    ? showAllMicros
      ? 'all'
      : 'selected'
    : internalView;

  // locks
  const lockedMacroSet = useMemo(() => new Set(lockedMacros), [lockedMacros]);
  const lockedMicroSet = useMemo(() => new Set(lockedMicros), [lockedMicros]);

  // allowed sets for restrict/readOnly
  const allowedMacros = useMemo(() => {
    if (!restrict) return null;
    return new Set(normInit.macros);
  }, [restrict, normInit.macros]);

  const allowedMicros = useMemo(() => {
    if (!restrict) return null;
    return new Set(normInit.micros);
  }, [restrict, normInit.micros]);

  // --- toggles ---
  const toggleMacro = (macroId) => {
    if (readOnly || !allowMacroSelect) return;
    const selected = macros.includes(macroId);
    if (selected && lockedMacroSet.has(macroId)) return;

    setMacros((prev) =>
      selected ? prev.filter((id) => id !== macroId) : [...prev, macroId]
    );

    // Picking a category after hiding everything brings its subcategories
    // back — otherwise there is no way to see what can be picked under it.
    if (!selected && !ctrlShowAll && internalView === 'none') {
      setInternalView('selected');
    }

    // purge its micros on unselect
    if (selected) {
      const cat = CATS.find((c) => c.id === macroId);
      const childMicros = subsOf(cat).map((m) => m.id);
      if (childMicros.length) {
        setMicros((prev) => prev.filter((m) => !childMicros.includes(m)));
      }
    }
  };

  const toggleMicro = (microId) => {
    if (readOnly || !allowMicroSelect) return;
    const selected = micros.includes(microId);
    if (selected && lockedMicroSet.has(microId)) return;

    setMicros((prev) =>
      selected ? prev.filter((id) => id !== microId) : [...prev, microId]
    );
  };

  // --- WHAT TO RENDER ---

  // MACROS: in interactive mode always render FULL catalog;
  // in restrict/readOnly render only allowed ones.
  const macroList = useMemo(() => {
    if (restrict && allowedMacros) {
      return CATS.filter((c) => allowedMacros.has(c.id));
    }
    return CATS; // <-- fix: show all macros in interactive mode
  }, [restrict, allowedMacros, CATS]);

  // MICROS come from all macros, only the selected ones, or none (microView).
  const microSourceMacros = useMemo(() => {
    if (!showMicros) return [];
    if (restrict && allowedMacros) {
      return CATS.filter((c) => allowedMacros.has(c.id));
    }
    if (microView === 'none') return [];
    if (microView === 'all') return CATS;
    return CATS.filter((c) => macros.includes(c.id));
  }, [showMicros, restrict, allowedMacros, microView, macros, CATS]);

  const microItems = useMemo(() => {
    return microSourceMacros.flatMap((cat) => {
      const list = subsOf(cat);
      if (!list.length) return [];
      const filtered =
        restrict && allowedMicros
          ? list.filter((m) => allowedMicros.has(m.id))
          : list;
      return filtered.map((micro) => ({ macro: cat, micro }));
    });
  }, [microSourceMacros, restrict, allowedMicros]);

  // selection helpers
  const isMacroSelected = (id) =>
    restrict ? allowedMacros?.has(id) : macros.includes(id);
  const isMicroSelected = (id) =>
    restrict ? allowedMicros?.has(id) : micros.includes(id);

  // toolbar helpers
  const visibleIdSet = useMemo(
    () => new Set(microItems.map(({ micro }) => micro.id)),
    [microItems]
  );
  const selectedVisibleCount = useMemo(
    () =>
      restrict
        ? Array.from(visibleIdSet).filter((id) => allowedMicros?.has(id)).length
        : micros.filter((id) => visibleIdSet.has(id)).length,
    [restrict, allowedMicros, micros, visibleIdSet]
  );
  const allVisibleSelected =
    visibleIdSet.size > 0 && selectedVisibleCount === visibleIdSet.size;

  const handleSelectClearAll = () => {
    if (readOnly || restrict) return;
    if (visibleIdSet.size === 0) return;

    if (allVisibleSelected) {
      setMicros((prev) =>
        prev.filter((id) => !visibleIdSet.has(id) || lockedMicroSet.has(id))
      );
    } else {
      const toAdd = Array.from(visibleIdSet);
      setMicros((prev) => {
        const s = new Set(prev);
        toAdd.forEach((id) => s.add(id));
        return Array.from(s);
      });
    }
  };

  const showToolbar = !(readOnly || restrict || !showMicros);

  // The button offers to hide whenever anything is showing, and to show all
  // otherwise.
  const canHide = ctrlShowAll ? showAllMicros : microItems.length > 0;
  const toggleShowAll = () => {
    if (ctrlShowAll) return setShowAllMicros((v) => !v);
    setInternalView(canHide ? 'none' : 'all');
  };

  // NEW: sizing styles
  const SIZE_STYLES = {
    xs: {
      containerSpace: 'space-y-3',
      macroGap: 'gap-2',
      microGap: 'gap-2',
      chipPad: 'p-1.5',
      macroIcon: 'w-5 h-5',
      microIcon: 'w-4 h-4',
      macroText: 'text-xs',
      microText: 'text-[11px]',
    },
    sm: {
      containerSpace: 'space-y-4',
      macroGap: 'gap-3',
      microGap: 'gap-2',
      chipPad: 'p-2',
      macroIcon: 'w-6 h-6',
      microIcon: 'w-5 h-5',
      macroText: 'text-xs',
      microText: 'text-xs',
    },
    md: {
      containerSpace: 'space-y-6',
      macroGap: 'gap-4',
      microGap: 'gap-3',
      chipPad: 'p-2',
      macroIcon: 'w-8 h-8',
      microIcon: 'w-6 h-6',
      macroText: 'text-sm',
      microText: 'text-xs',
    },
    lg: {
      containerSpace: 'space-y-8',
      macroGap: 'gap-5',
      microGap: 'gap-4',
      chipPad: 'p-3',
      macroIcon: 'w-10 h-10',
      microIcon: 'w-8 h-8',
      macroText: 'text-base',
      microText: 'text-sm',
    },
  };
  const sz = SIZE_STYLES[size] || SIZE_STYLES.md;

  return (
    <div className={sz.containerSpace}>
      {/* MACROS */}
      <div>
        {!noTitle && (
          <h3 className="text-sm font-semibold mb-2">
            {t('listing.categories')}
          </h3>
        )}
        <div className={`flex flex-wrap ${sz.macroGap}`}>
          {macroList.map((cat) => {
            const selected = isMacroSelected(cat.id);
            const Chip = (
              <div
                className={`rounded-lg ${sz.chipPad} ${readOnly ? 'border border-gray-300' : ''} ${white ? 'bg-white' : ''} flex items-center space-x-2 transition`}
              >
                <CategoryIcon
                  macro={cat.id}
                  className={`${sz.macroIcon} ${selected ? cat.color?.text : ''}`}
                />
                <span
                  className={`${sz.macroText} font-medium ${
                    selected ? cat.color?.text : 'text-gray-700'
                  }`}
                >
                  {getMacroName(cat.id)}
                </span>
              </div>
            );

            return (
              <div key={cat.id} className="relative group">
                {readOnly ? (
                  Chip
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleMacro(cat.id)}
                    className={`rounded-lg p-0 border ${
                      selected
                        ? `${cat.color?.bg} ${cat.color?.border}`
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {Chip}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* MICROS */}
      {showMicros && (
        <div>
          <div className="flex items-center justify-between mb-2">
            {!noMicroTitle && (
              <h3 className="text-sm font-semibold">
                {t('listing.subcategories')}
              </h3>
            )}
            {showToolbar && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSelectClearAll}
                  disabled={visibleIdSet.size === 0}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border rounded-lg transition outline-none ${
                    visibleIdSet.size === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white hover:bg-gray-100 text-gray-700'
                  }`}
                  title={
                    allVisibleSelected
                      ? t('listing.clear_all')
                      : t('listing.select_all')
                  }
                >
                  {allVisibleSelected ? (
                    <>
                      <XCircleIcon className="w-4 h-4" />
                      <span>{t('listing.clear_all')}</span>
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      <span>{t('listing.select_all')}</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={toggleShowAll}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border bg-white rounded-lg transition outline-none hover:bg-gray-100"
                >
                  {canHide ? (
                    <>
                      <ChevronUpIcon className="w-4 h-4" />
                      <span>{t('listing.hide_all_subcategories')}</span>
                    </>
                  ) : (
                    <>
                      <ChevronDownIcon className="w-4 h-4" />
                      <span>{t('listing.show_all_subcategories')}</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className={`flex flex-wrap ${sz.microGap}`}>
            {microItems.map(({ macro, micro }) => {
              const selected = isMicroSelected(micro.id);
              const Chip = (
                <div
                  className={`rounded-lg ${sz.chipPad} ${readOnly ? 'border border-gray-300' : ''} ${white ? 'bg-white' : ''} flex items-center space-x-2 transition`}
                >
                  <CategoryIcon
                    macro={macro.id}
                    microCat={micro.id}
                    className={`${sz.microIcon} ${selected ? macro.color?.text : ''}`}
                  />
                  <span
                    className={`text-xs ${sz.microText} ${selected ? macro.color?.text : ''}`}
                  >
                    {getMicroName(micro.id)}
                  </span>
                </div>
              );

              return (
                <div key={micro.id} className="relative group">
                  {readOnly ? (
                    Chip
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleMicro(micro.id)}
                      className={`rounded-lg p-0 border ${
                        selected
                          ? `${macro.color?.bg} ${macro.color?.border}`
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {Chip}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategorySelectorGrid;
